/**
 * Le micro de l'élève, tenu jusqu'à son geste : quand la salle peut-elle l'ouvrir d'elle-même ?
 *
 * La salle ouvre le micro à sa connexion, puis le règle au mode (`MuteCurrentUser`). Ces demandes ne
 * sont faites dans aucun geste de l'élève. Deux cas où elles ne doivent pas partir :
 *  - **l'élève l'a coupé avant d'entrer** (la salle d'attente, `liveMode.isAudioMuted`) : il le rouvre
 *    lui-même, ou son masteur le repasse en Dialogue — comme un micro coupé en séance ;
 *  - **l'ouvrir poserait une question au navigateur** : le live rouvert par un rechargement, sans
 *    l'entrée (prod, 22/10, Safari réglé sur « Demander »). La fenêtre surgissait au milieu du live,
 *    sans un mot, et un refus valait pour toute la page. Le micro attend alors un geste : en Dialogue,
 *    une boîte dit pourquoi et le demande dans le toucher ; le micro de la barre fait de même.
 *
 * « L'accès est acquis » quand la demande ne posera aucune question : le micro a déjà été obtenu dans
 * la page (le test d'entrée, le live), ou le navigateur le dit accordé sans rien ouvrir. Une fois acquis,
 * il le reste pour le live. Tant qu'on le lit (quelques millisecondes), le micro est tenu sans boîte.
 *
 * La règle : **la salle n'ouvre le micro d'elle-même que si l'élève ne l'a pas coupé et que l'accès est
 * acquis**. Le micro ouvert (par l'élève, ou par la salle une fois libre) l'acquiert.
 *
 * Le choix de la salle d'attente n'est pas un état de la règle : il se lit dans le magasin, que l'ouverture
 * du micro et le passage en Dialogue effacent (`useMicGate`).
 *
 * Le drapeau que la salle lit (`liveKitRoom.microphoneHeld`) : écrit par ce seul live, il naît à `false`,
 * n'est pas persisté et revient à `false` au démontage et à la sortie ; le tuteur, l'ancien live et la
 * messagerie publient donc comme avant.
 */

/** L'accès au micro : acquis, encore lu, ou à demander dans un geste. */
export type MicAccess = "ready" | "reading" | "ask"

export type MicGateState = {
    access: MicAccess
    /** « Plus tard » : la boîte fermée jusqu'au prochain passage en Dialogue. */
    dismissed: boolean
}

export type MicGateEvent =
    /** L'accès lu sans rien ouvrir : accordé (`ready`), ou il faudra le demander. */
    | { type: "accessRead"; ready: boolean }
    /** Le micro s'est ouvert : l'accès est acquis. */
    | { type: "micOpened" }
    | { type: "dialogueEntered" }
    | { type: "dismissed" }

export function initialMicGate(accessReady: boolean): MicGateState {
    return { access: accessReady ? "ready" : "reading", dismissed: false }
}

export function micGateReducer(state: MicGateState, event: MicGateEvent): MicGateState {
    switch (event.type) {
        case "accessRead":
            if (state.access === "ready") return state
            return { ...state, access: event.ready ? "ready" : "ask" }
        case "micOpened":
            return state.access === "ready" && !state.dismissed ? state : { access: "ready", dismissed: false }
        case "dialogueEntered":
            // Le masteur veut l'entendre : un micro à demander rouvre sa boîte.
            return state.dismissed ? { ...state, dismissed: false } : state
        case "dismissed":
            return state.dismissed ? state : { ...state, dismissed: true }
    }
}

export type MicGateView = {
    /** La salle ne doit pas ouvrir le micro d'elle-même (`liveKitRoom.microphoneHeld`). */
    held: boolean
    /**
     * « Active ton micro » : en Dialogue, la salle connectée, quand l'accès est à demander, que l'élève n'a
     * pas coupé son micro avant d'entrer et qu'aucun refus n'est déjà dit. Pas avant la connexion : la
     * salle, en se connectant, couperait le micro ouvert par le toucher (elle publie un micro tenu à
     * l'arrêt), et il faudrait un second toucher.
     */
    ask: boolean
}

export function micGateView(
    state: MicGateState,
    live: { chosenOff: boolean; dialogue: boolean; refused: boolean; connected: boolean }
): MicGateView {
    return {
        held: live.chosenOff || state.access !== "ready",
        ask:
            !live.chosenOff &&
            state.access === "ask" &&
            live.dialogue &&
            live.connected &&
            !state.dismissed &&
            !live.refused,
    }
}
